import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, FileClock, LogOut, LineChart, Plug, CreditCard, Coins, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/reports", label: "Saved Reports", icon: FileClock },
  { to: "/trends", label: "Trends", icon: LineChart },
  { to: "/wordpress", label: "WordPress", icon: Plug },
  { to: "/search-console", label: "Search Console", icon: Search },
  { to: "/billing", label: "Billing", icon: CreditCard },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex bg-[#F9F9FA]">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-gray-200 bg-white sticky top-0 h-screen">
        <div className="px-6 h-16 flex items-center gap-2 border-b border-gray-200">
          <img src="/logo.png" alt="Sapientia" className="w-8 h-8 object-contain" />
          <span className="font-heading font-black text-xl tracking-tighter">Sapientia</span>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {nav.map((n) => {
            const active = location.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  active ? "bg-klein text-white" : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-4.5 h-4.5" strokeWidth={1.75} />
                {n.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-40 flex items-center justify-between px-6">
          <div className="md:hidden flex items-center gap-2">
            <img src="/logo.png" alt="Sapientia" className="w-7 h-7 object-contain" />
            <span className="font-heading font-black text-lg">Sapientia</span>
          </div>
          <div className="flex-1" />
          <Link
            to="/billing"
            data-testid="credits-pill"
            className="flex items-center gap-1.5 bg-klein/10 text-klein font-bold text-sm px-3 py-1.5 rounded-full hover:bg-klein/20 transition-colors mr-3"
          >
            <Coins className="w-4 h-4" />
            {user?.credits ?? 0} <span className="hidden sm:inline font-medium text-klein/70">{(user?.credits ?? 0) === 1 ? "credit" : "credits"}</span>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-menu-button"
                className="flex items-center gap-2 rounded-full hover:bg-gray-100 pl-1 pr-3 py-1 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center text-xs font-bold">
                  {initials}
                </div>
                <span className="text-sm font-medium hidden sm:block">{user?.name || user?.email}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs text-gray-500 font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="logout-button"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                className="text-bad cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-6 md:p-8 lg:p-10">{children}</main>
      </div>
    </div>
  );
}
